/**
 * "Tuo Kuutio" / "Tuo tietoaineisto": upload a clone package, see what it holds and
 * whether this server can take it, then name the clone. A taken name keeps the upload
 * so another name can be tried. specs/clone-packages.md
 */
import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { Button, Actions, StatusLine, FieldHelp, Label, Input } from '../InstanceSettings/formPrimitives';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import CloneNameField from './CloneNameField';
import CloneResult from './CloneResult';
import { uploadPackage, importUploadedPackage } from './cloneApi';

function Summary({ summary }) {
  const { t } = useTranslation();
  const counts = Object.values(summary.counts || {});
  const rows = counts.reduce((a, b) => a + b, 0);
  return (
    <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
      <strong>{summary.source?.name}</strong>
      <span>{t('clone.packageFrom', { env: summary.source?.env, date: (summary.createdAt || '').slice(0, 10) })}</span>
      {summary.mode && <span>{t('clone.modeIs', { mode: t(summary.mode === 'everything' ? 'clone.modeEverything' : 'clone.modePrepared') })}</span>}
      <span>{t('clone.packageRows', { count: rows, tables: counts.length })}</span>
      {summary.missingDatasets?.length > 0 && (
        <FieldHelp>{t('clone.missingDatasets', { ids: summary.missingDatasets.join(', ') })}</FieldHelp>
      )}
      {summary.schemaProblems?.length > 0 && (
        <StatusLine as="div" $error>
          {t('clone.schemaProblems')}
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{summary.schemaProblems.map((p) => <li key={p}>{p}</li>)}</ul>
        </StatusLine>
      )}
    </div>
  );
}

export default function ImportPackagePanel({ kind, onOpenClone }) {
  const { t } = useTranslation();
  const [upload, setUpload] = useState(null); // { uploadId, summary }
  const [name, setName] = useState('');
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(null); // 'read' | 'import'
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const choose = async (file) => {
    if (!file) return;
    setBusy('read');
    setError(null);
    setUpload(null);
    setResult(null);
    try { setUpload(await uploadPackage(kind, file)); } catch (err) { setError(err.message); } finally { setBusy(null); }
  };

  const runImport = async () => {
    setBusy('import');
    setError(null);
    try {
      setResult(await importUploadedPackage(upload.uploadId, name));
      setUpload(null);
      setName('');
    } catch (err) {
      setError(err.message);
      if (err.code === 'upload_expired') setUpload(null);
    } finally {
      setBusy(null);
    }
  };

  const blocked = upload?.summary?.schemaProblems?.length > 0;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <FieldHelp style={{ margin: 0 }}>{t(kind === 'dataset' ? 'clone.importLeadDataset' : 'clone.importLeadInstance')}</FieldHelp>
      <div>
        <Label htmlFor={`clone-file-${kind}`}>{t('clone.fileLabel')}</Label>
        <Input id={`clone-file-${kind}`} type="file" accept=".zip,application/zip" disabled={!!busy}
          onChange={(e) => choose(e.target.files?.[0])} />
        {busy === 'read' && <FieldHelp>{t('clone.reading')}</FieldHelp>}
      </div>

      {upload && (
        <>
          <Summary summary={upload.summary} />
          {!blocked && (
            <>
              <CloneNameField id={`clone-import-name-${kind}`} kind={kind} value={name} onChange={setName} onAvailable={setAvailable}
                placeholder={t('clone.namePlaceholder', { name: upload.summary.source?.name || '' })} />
              <Actions>
                <Button type="button" $variant="primary" onClick={runImport} disabled={!available || !!busy}>
                  <Upload /> {busy === 'import' ? t('clone.importing') : t('clone.importButton')}
                </Button>
              </Actions>
            </>
          )}
        </>
      )}

      {result && <CloneResult result={result} onOpen={onOpenClone} />}
      {error && <StatusLine $error>{t('clone.failed', { error })}</StatusLine>}
    </div>
  );
}
